import { doc, onSnapshot } from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { normalizeCode } from "./connect-code";
import { useConnectionRequest } from "./use-connection-request";

export type MyCodeState =
  | { status: "loading" }
  /** No code yet; ISSUE_CONNECT_CODE is on its way. */
  | { status: "issuing"; queued: boolean }
  | { status: "ready"; code: string; rotating: boolean }
  | { status: "error"; error: string };

/**
 * The person's own connect code, read live from `users/{uid}.connectCode`.
 *
 * Codes are minted lazily: the first time the sheet opens without one, it
 * files ISSUE_CONNECT_CODE, once per mount. The profile document is in the
 * persistent cache after the first read, so the sheet still shows a code with
 * the network gone.
 */
export function useMyConnectCode(uid: string, active: boolean) {
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const [readError, setReadError] = useState<string | null>(null);
  const issue = useConnectionRequest(uid);
  const rotate = useConnectionRequest(uid);
  const issued = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    return onSnapshot(
      doc(db, "users", uid),
      (snapshot) => {
        const value = snapshot.get("connectCode");
        setCode(typeof value === "string" ? normalizeCode(value) : null);
        setReadError(null);
      },
      (error) => setReadError(error.message),
    );
  }, [uid, active]);

  const submitIssue = issue.submit;
  useEffect(() => {
    if (!active || code !== null || issued.current) return;
    issued.current = true;
    submitIssue("ISSUE_CONNECT_CODE");
  }, [active, code, submitIssue]);

  const rotating =
    rotate.state.status === "queued" || rotate.state.status === "waiting";
  const submitRotate = rotate.submit;
  const newCode = useCallback(() => {
    submitRotate("ROTATE_CONNECT_CODE");
  }, [submitRotate]);

  let state: MyCodeState;
  if (readError) state = { status: "error", error: readError };
  else if (issue.state.status === "failed" && !code) {
    state = { status: "error", error: issue.state.error };
  } else if (code === undefined) state = { status: "loading" };
  else if (code === null) {
    state = { status: "issuing", queued: issue.state.status === "queued" };
  } else state = { status: "ready", code, rotating };

  return {
    state,
    newCode,
    rotateError: rotate.state.status === "failed" ? rotate.state.error : null,
  };
}
